import { DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { Segmented } from 'antd';
import { useColorScheme, type ColorSchemePreference } from './color-scheme';

/**
 * Claro, escuro ou o do sistema, em três botões.
 *
 * O `ColorSchemeToggle` que já existe é um botão que alterna dois estados, e
 * ele resolve o caso do app: quem está estudando quer virar o tema num toque.
 * Não resolve o caso da landing, onde a terceira opção importa - **"Sistema" é
 * a única que diz que a página respeita a escolha do aparelho**, e sem ela quem
 * usa o escuro no sistema não tem como pedir de volta o comportamento
 * automático depois de ter tocado no seletor.
 *
 * Três botões e não um menu: são três estados exclusivos e curtos, que é
 * exatamente o que um `Segmented` mostra melhor que um `Dropdown` - e cabem em
 * 91px com só os ícones.
 */
const OPCOES: { value: ColorSchemePreference; icone: React.ReactNode; titulo: string }[] = [
  { value: 'light', icone: <SunOutlined />, titulo: 'Tema claro' },
  { value: 'dark', icone: <MoonOutlined />, titulo: 'Tema escuro' },
  { value: 'system', icone: <DesktopOutlined />, titulo: 'Acompanhar o sistema' },
];

export function SeletorDeTema({
  comRotulo = false,
  tamanho = 'small',
}: {
  /** Troca o ícone pelo nome do tema. Na gaveta do celular há espaço; no cabeçalho, não. */
  comRotulo?: boolean;
  tamanho?: 'small' | 'middle' | 'large';
}) {
  const { preference, setPreference } = useColorScheme();

  return (
    <Segmented<ColorSchemePreference>
      size={tamanho}
      value={preference}
      onChange={setPreference}
      aria-label="Tema da página"
      /*
       * O ícone vai pela prop `icon` e a dica pela prop `tooltip`, as duas do
       * próprio Antd - e não num `label` montado à mão.
       *
       * **A versão à mão encostava o ícone no topo.** O botão do `Segmented`
       * não centraliza nada: ele reserva a altura com `min-height` e deixa o
       * `line-height` cuidar do resto, o que só funciona enquanto o conteúdo
       * for de linha. O `<span style={{ display: 'flex' }}>` que embrulhava o
       * ícone virava um bloco de 15px dentro de uma caixa de 20px, e bloco não
       * responde a `line-height`: ele encostava em cima e os 5px de sobra iam
       * todos para baixo.
       *
       * De quebra a dica melhora. Embrulhada no `label`, ela só aparecia com o
       * ponteiro em cima do desenho do ícone; pela prop, o Antd embrulha o
       * botão inteiro, que é a área que a pessoa mira.
       *
       * **Com rótulo, o ícone sai.** Ele acompanhava o nome, e os três botões
       * pediam 268px dentro dos 232px de conteúdo da gaveta - "Sistema" ficava
       * cortado na borda e a gaveta ganhava barra de rolagem horizontal. Sem
       * ícone são 205px, e a palavra já diz o que o desenho diria. Pelo mesmo
       * motivo não há dica aqui: sobre um nome ela é redundante, e dica
       * redundante só atrapalha o toque.
       */
      options={OPCOES.map((opcao) =>
        comRotulo
          ? { value: opcao.value, label: nomeDoTema(opcao.value) }
          : { value: opcao.value, icon: opcao.icone, tooltip: opcao.titulo },
      )}
    />
  );
}

function nomeDoTema(valor: ColorSchemePreference): string {
  if (valor === 'light') return 'Claro';
  if (valor === 'dark') return 'Escuro';
  return 'Sistema';
}
